import { EnergySourceWithId } from "@/hooks/energy-sources/useEnergySourceTable";
import { Column } from "@/components/ui/data-table/types";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatNumberDynamic } from "@/utils/numberFormatter";

export const useEnergySourceColumns = (
  t: (key: string) => string,
  onEdit: (energySource: EnergySourceWithId) => void,
  onDelete: (energySource: EnergySourceWithId) => void
): Column<EnergySourceWithId>[] => {
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  
  const baseColumns: Column<EnergySourceWithId>[] = [
    {
      header: t("energySources.table.name"),
      accessorKey: "name",
      sortable: true,
      className: "min-w-[180px]",
    },
    {
      header: `${t("energySources.table.emission_factor")} (kg CO₂eq/kWh)`,
      accessorKey: "carbon_factor",
      sortable: true,
      className: "min-w-[200px]",
      cell: (_: string, row: EnergySourceWithId) => (
        <div>{row.carbon_factor ? formatNumberDynamic(row.carbon_factor.value) : "-"}</div>
      ),
    },
    {
      header: `${t("energySources.table.hydric_factor")} (${t("energySources.table.hydric_factor_unit")})`,
      accessorKey: "hydric_factor",
      sortable: true,
      className: "min-w-[180px]",
      cell: (_: string, row: EnergySourceWithId) => (
        <div>{row.hydric_factor ? formatNumberDynamic(row.hydric_factor.value) : "-"}</div>
      ),
    },
  ];

  const actionsColumn: Column<EnergySourceWithId> = {
    header: t("energySources.table.actions"),
    accessorKey: "id",
    sortable: false,
    className: "w-[100px]",
    cell: (_: string, row: EnergySourceWithId) => (
      <div className="flex items-center gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(row);
                }}
                aria-label={t("energySources.actions.edit")}
              >
                <Pencil className="h-4 w-4 text-teal-600" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("energySources.actions.edit")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(row);
                }}
                aria-label={t("energySources.actions.delete")}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("energySources.actions.delete")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
  };

  return isAssimagraUser ? baseColumns : [...baseColumns, actionsColumn];
};
